import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Bug } from "lucide-react";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { ShareSheet, getShareCopy } from "@/components/shared/ShareSheet";
import PostClaimFeedback from "@/components/PostClaimFeedback";
import WaitTimeSheet from "@/components/shared/WaitTimeSheet";
import { toast } from "sonner";
import { trackError } from "@/lib/track";
import { useOfferDetail } from "./OfferDetailContext";

export default function OfferModals() {
  const {
    offer, businessId, mode,
    showVerifyModal, setShowVerifyModal,
    showShare, setShowShare,
    showBugReport, setShowBugReport,
    showFeedback, setShowFeedback,
    showWaitSheet, setShowWaitSheet,
    submitting, setSubmitting,
    priceReportStep, setPriceReportStep,
    prActualValue, setPrActualValue,
    prWhatReceived, setPrWhatReceived,
    prLocationCity, setPrLocationCity,
    prNotes, setPrNotes,
    checklistSteps, setChecklistSteps,
    fetchOffer,
  } = useOfferDetail();

  // Bug report form state — self-contained in this modal
  const [bugIssueType, setBugIssueType] = useState("");
  const [bugDescription, setBugDescription] = useState("");
  const [bugSubmitting, setBugSubmitting] = useState(false);

  return (
    <>
      {/* Verify/Price Report Modal */}
      <AnimatePresence>
        {showVerifyModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[200] flex items-end" onClick={() => { setShowVerifyModal(false); setPriceReportStep(false); }}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} className="w-full bg-white rounded-t-3xl p-5 pb-10 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 pb-1">
                <BrandLogo name={offer.name} logoUrl={offer.logo_url} size="md" />
                <div>
                  <p className="text-xs text-slate-400 font-medium">Price Report (Optional)</p>
                  <h3 className="text-base font-bold text-slate-800 leading-tight">{offer.name}</h3>
                </div>
              </div>
              <>
                <p className="text-xs text-slate-400">Help us keep prices accurate. Quick check:</p>
                <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Listed value</span>
                  <span className="text-sm font-bold text-slate-700">{offer.offer_value_est > 0 ? `$${offer.offer_value_est.toFixed(2)}` : "% discount"}</span>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Actual value you received ($)</label>
                  <input type="number" step="0.01" min="0" value={prActualValue} onChange={(e) => setPrActualValue(e.target.value)} className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-sky-400 outline-none mt-0.5" placeholder="e.g. 7.50" data-testid="pr-actual-value" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">What did you receive?</label>
                  <select value={prWhatReceived} onChange={(e) => setPrWhatReceived(e.target.value)} className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-sky-400 outline-none mt-0.5" data-testid="pr-what-received">
                    <option value="">Select...</option>
                    <option value="Exactly as described">Exactly as described</option>
                    <option value="Free item (different)">Free item (different from listed)</option>
                    <option value="Discount/coupon">Discount or coupon</option>
                    <option value="Points/credits">Points or credits</option>
                    <option value="Nothing — was denied">Nothing — was denied</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Which location? (full address)</label>
                  <input type="text" value={prLocationCity} onChange={(e) => setPrLocationCity(e.target.value)} className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-sky-400 outline-none mt-0.5" placeholder="e.g. 2280 Arden Way, Sacramento, CA" data-testid="pr-location" />
                  {offer.locations?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {offer.locations.slice(0, 5).map((loc) => {
                        const addr = [loc.address, loc.city, loc.state].filter(v => v && v !== "nan").join(", ");
                        return addr ? (
                          <button key={loc.location_id} type="button" onClick={() => setPrLocationCity(addr)} className="px-2 py-1 bg-sky-50 text-sky-600 rounded-lg text-[10px] font-medium border border-sky-100 hover:bg-sky-100 transition" data-testid={`pr-loc-${loc.location_id}`}>{loc.city || loc.location_name}</button>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Notes (optional)</label>
                  <input type="text" value={prNotes} onChange={(e) => setPrNotes(e.target.value)} className="w-full px-4 py-3 bg-slate-50 rounded-xl text-sm border border-slate-200 focus:border-sky-400 outline-none mt-0.5" placeholder="Anything we should know?" data-testid="pr-notes" />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { setShowVerifyModal(false); setPriceReportStep(false); setChecklistSteps({}); toast.success("Verification submitted!"); }} className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm" data-testid="pr-skip">Skip</button>
                  <button
                    onClick={async () => {
                      if (!prActualValue || !prWhatReceived) { toast.error("Please fill in value and what you received"); return; }
                      setSubmitting(true);
                      try {
                        await axios.post(`${API}/price-report`, {
                          business_id: offer.business_id,
                          actual_value: parseFloat(prActualValue),
                          what_received: prWhatReceived,
                          location_city: prLocationCity,
                          notes: prNotes,
                        });
                        toast.success("Price report submitted! +10 pts");
                        setShowVerifyModal(false);
                        setPriceReportStep(false);
                        setChecklistSteps({});
                      } catch (e) { toast.error("Failed to submit report"); trackError("price_report_submit", e, { business_id: businessId }); } finally { setSubmitting(false); }
                    }}
                    disabled={submitting}
                    className="flex-1 py-3 rounded-xl bg-sky-500 text-white font-semibold shadow-lg text-sm disabled:opacity-50"
                    data-testid="pr-submit"
                  >{submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Submit Report"}</button>
                </div>
              </>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Sheet */}
      <ShareSheet
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        url={`${window.location.origin}/offer/${offer.business_id}`}
        title={getShareCopy(mode, "offer", { name: offer.name, offerTitle: offer.offer_title }).title}
        description={getShareCopy(mode, "offer", { name: offer.name, offerTitle: offer.offer_title }).description}
        businessId={offer.business_id}
        businessName={offer.name}
        shareContext="offer"
      />

      {/* Bug Report Modal */}
      <AnimatePresence>
        {showBugReport && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[200] flex items-end" onClick={() => setShowBugReport(false)}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} className="w-full bg-white rounded-t-3xl p-6 pb-10 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 pb-1">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Bug className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Report an Issue</h3>
                  <p className="text-[10px] text-slate-400">Earn 10 pts now, +15 bonus if validated</p>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">What's wrong?</label>
                <select value={bugIssueType} onChange={(e) => setBugIssueType(e.target.value)} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="bug-issue-type">
                  <option value="">Select issue type...</option>
                  <option value="wrong_price">Wrong price / value</option>
                  <option value="dead_link">Dead link / broken website</option>
                  <option value="offer_expired">Offer no longer available</option>
                  <option value="business_closed">Business closed</option>
                  <option value="wrong_info">Wrong information (hours, address, etc.)</option>
                  <option value="wrong_category">Wrong category</option>
                  <option value="missing_details">Missing important details</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Details (optional)</label>
                <textarea value={bugDescription} onChange={(e) => setBugDescription(e.target.value)} placeholder="Tell us more about the issue..." rows={3} className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" data-testid="bug-description" />
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => { setShowBugReport(false); setBugIssueType(""); setBugDescription(""); }} className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm" data-testid="bug-cancel">Cancel</button>
                <button
                  onClick={async () => {
                    if (!bugIssueType) return;
                    setBugSubmitting(true);
                    try {
                      await axios.post(`${API}/bug-reports`, {
                        business_id: offer.business_id,
                        business_name: offer.name,
                        issue_type: bugIssueType,
                        description: bugDescription,
                      });
                      toast.success("Report submitted! +10 pts. Bonus if validated.");
                      setShowBugReport(false);
                      setBugIssueType("");
                      setBugDescription("");
                    } catch (e) { toast.error("Failed to submit report"); trackError("bug_report_submit", e, { business_id: businessId }); }
                    finally { setBugSubmitting(false); }
                  }}
                  disabled={!bugIssueType || bugSubmitting}
                  className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-1.5"
                  data-testid="bug-submit"
                >
                  {bugSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bug className="w-4 h-4" />}
                  Submit Report
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Post Claim Feedback */}
      <PostClaimFeedback
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        businessId={businessId}
        businessName={offer?.name}
      />

      {/* Wait Time Sheet */}
      {showWaitSheet && (
        <WaitTimeSheet
          businessId={businessId}
          businessName={offer?.name}
          onClose={() => setShowWaitSheet(false)}
          onSuccess={() => { fetchOffer(); }}
        />
      )}
    </>
  );
}
